import React, { useState } from "react";
import { Accessibility, Type, Contrast, Link2, Sparkles, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useA11y } from "@/context/AccessibilityContext";
import { useLang } from "@/context/LanguageContext";

const SizeBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-colors duration-200 no-a11y-underline ${
      active ? "bg-brand text-brand-cream border-brand" : "bg-white text-brand-ink border-brand/20 hover:border-brand"
    }`}
  >
    {children}
  </button>
);

const Row = ({ icon: Icon, label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4 py-3.5 border-b border-brand/10 last:border-0">
    <div className="flex items-center gap-3">
      <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-soft/60 text-brand">
        <Icon className="h-4 w-4" />
      </span>
      <span className="font-semibold text-brand-ink">{label}</span>
    </div>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </div>
);

export const AccessibilityPanel = () => {
  const [open, setOpen] = useState(false);
  const { settings, update, reset } = useA11y();
  const { t } = useLang();
  const a = t.a11y;

  return (
    <>
      <button
        data-testid="a11y-fab"
        onClick={() => setOpen(true)}
        aria-label={a.open}
        className="fixed z-50 bottom-5 left-5 grid place-items-center h-14 w-14 rounded-full bg-brand text-brand-cream shadow-[0_10px_30px_rgba(134,77,99,0.35)] hover:bg-brand-hover hover:scale-105 transition-[transform,background-color] duration-200 no-a11y-underline"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-brand-cream w-[92vw] sm:max-w-md" data-testid="a11y-panel">
          <SheetHeader className="text-left">
            <SheetTitle className="font-heading text-2xl text-brand-ink flex items-center gap-2">
              <Accessibility className="h-6 w-6 text-brand" /> {a.title}
            </SheetTitle>
            <SheetDescription className="text-brand-ink/60">{a.subtitle}</SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            <p className="text-sm font-bold text-brand-ink mb-2 flex items-center gap-2">
              <Type className="h-4 w-4 text-brand" /> {a.text_size}
            </p>
            <div className="flex gap-2">
              <SizeBtn testid="a11y-size-normal" active={settings.fontSize === "normal"} onClick={() => update({ fontSize: "normal" })}>
                {a.normal}
              </SizeBtn>
              <SizeBtn testid="a11y-size-large" active={settings.fontSize === "large"} onClick={() => update({ fontSize: "large" })}>
                {a.large}
              </SizeBtn>
              <SizeBtn testid="a11y-size-xl" active={settings.fontSize === "xl"} onClick={() => update({ fontSize: "xl" })}>
                {a.xl}
              </SizeBtn>
            </div>
          </div>

          <div className="mt-6">
            <Row icon={Contrast} label={a.contrast} checked={settings.contrast} onChange={(v) => update({ contrast: v })} testid="a11y-contrast-toggle" />
            <Row icon={Link2} label={a.links} checked={settings.links} onChange={(v) => update({ links: v })} testid="a11y-links-toggle" />
            <Row icon={Sparkles} label={a.motion} checked={settings.motion} onChange={(v) => update({ motion: v })} testid="a11y-motion-toggle" />
          </div>

          <Button
            data-testid="a11y-reset-btn"
            onClick={reset}
            variant="outline"
            className="mt-8 w-full rounded-full border-brand/30 text-brand hover:bg-brand-soft/40 font-bold no-a11y-underline"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> {a.reset}
          </Button>
        </SheetContent>
      </Sheet>
    </>
  );
};
